import { useEffect, useRef } from 'react';

import { FormikErrors, FormikValues } from 'formik';

const DATA_FORMIK_FIELD = 'data-formik-field';

/**
 * Returns the first error field key from Formik errors (supports nested keys like 'profile.age').
 */
export function getFirstErrorFieldKey(
  errors: FormikErrors<unknown>,
): string | null {
  if (!errors || typeof errors !== 'object') {
    return null;
  }
  const keys = Object.keys(errors);
  for (const key of keys) {
    const val = (errors as Record<string, unknown>)[key];
    if (typeof val === 'string') {
      return key;
    }
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      const nested = getFirstErrorFieldKey(val as FormikErrors<unknown>);
      if (nested) {
        return `${key}.${nested}`;
      }
    }
  }
  return null;
}

/**
 * Finds a DOM element that represents the form field (by id, name, or data-formik-field).
 * Use data-formik-field on wrappers for custom fields that don't have a native input.
 * For nested keys (e.g. programSetItems.0.sets), tries exact key then base path (e.g. programSetItems).
 */
export function findElementForField(fieldKey: string): HTMLElement | null {
  const byId = document.getElementById(fieldKey);
  if (byId) return byId;
  const byName = document.querySelector<HTMLElement>(`[name="${fieldKey}"]`);
  if (byName) return byName;
  const byData = document.querySelector<HTMLElement>(
    `[${DATA_FORMIK_FIELD}="${fieldKey}"]`,
  );
  if (byData) return byData;
  // Nested key (e.g. programSetItems.0.sets): try base segment so we can scroll to the table/section
  const baseKey = fieldKey.split('.')[0];
  if (baseKey !== fieldKey) {
    const byBaseId = document.getElementById(baseKey);
    if (byBaseId) return byBaseId;
    const byBaseData = document.querySelector<HTMLElement>(
      `[${DATA_FORMIK_FIELD}="${baseKey}"]`,
    );
    if (byBaseData) return byBaseData;
  }
  return null;
}

export interface UseScrollToFirstErrorOptions {
  /** Scroll behavior. Default: 'smooth' */
  behavior?: ScrollBehavior;
  /** Block position. Default: 'center' */
  block?: ScrollLogicalPosition;
  /** Optional ordered list of field names; first field in this list that has an error is scrolled to. */
  fieldOrder?: string[];
}

/**
 * Hook that scrolls to the first invalid field when form submit fails (validation errors).
 * Use with Formik: pass formik bag and call after submit. Trigger is when submitCount
 * increases and errors exist.
 *
 * Convention: fields are found by id, name, or data-formik-field attribute.
 * For custom field wrappers (e.g. file upload), wrap in an element with data-formik-field="fieldName".
 */
export function useScrollToFirstError(
  formik: FormikValues,
  options: UseScrollToFirstErrorOptions = {},
): void {
  const { behavior = 'smooth', block = 'center', fieldOrder } = options;
  const prevSubmitCountRef = useRef(formik.submitCount);

  useEffect(() => {
    const errors = formik.errors as FormikErrors<unknown>;
    const hasErrors = errors && Object.keys(errors).length > 0;
    const submitJustHappened = formik.submitCount > prevSubmitCountRef.current;

    if (!submitJustHappened || !hasErrors) {
      if (submitJustHappened) {
        prevSubmitCountRef.current = formik.submitCount;
      }
      return;
    }

    prevSubmitCountRef.current = formik.submitCount;

    const fieldKey = fieldOrder
      ? fieldOrder.find((name) => getFieldError(errors, name))
      : getFirstErrorFieldKey(errors);

    if (!fieldKey) return;

    const el = findElementForField(fieldKey);
    if (el) {
      el.scrollIntoView({ behavior, block });
    }
  }, [formik.submitCount, formik.errors, behavior, block, fieldOrder]);
}

/** Returns truthy if the field (or any nested path under it) has an error. */
function getFieldError(
  errors: FormikErrors<unknown>,
  fieldPath: string,
): unknown {
  const parts = fieldPath.split('.');
  let current: unknown = errors;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  if (typeof current === 'string') return current;
  if (current && typeof current === 'object' && Object.keys(current).length > 0)
    return true;
  return undefined;
}
